import {
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { AlertTriangle, User, Building2, Check, Eye } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';

import { useInvestorImportDetectConflicts } from '@/hooks/react-query/useInvestorImport';
import type {
  ImportValidationResult,
  ExistingInvestorConflict,
  FundMatchingData,
} from '../../types/investor-import';

interface ConflictResolutionStepProps {
  sessionId: string;
  validationResult: ImportValidationResult;
  fundMatches: FundMatchingData[];
  onConflictsResolved: (conflicts: ExistingInvestorConflict[]) => void;
}

interface ConflictFieldMapping {
  [key: string]: {
    label: string;
    getExistingValue: (conflict: ExistingInvestorConflict) => string;
    getImportValue: (conflict: ExistingInvestorConflict) => string;
  };
}

export function ConflictResolutionStep({
  sessionId,
  validationResult,
  fundMatches,
  onConflictsResolved,
}: ConflictResolutionStepProps) {
  const [overwriteOverrides, setOverwriteOverrides] = useState<
    Record<string, boolean>
  >({});
  const [expandedConflicts, setExpandedConflicts] = useState<Set<string>>(
    () => new Set()
  );
  const hasLoadedConflictsRef = useRef(false);

  const detectConflictsMutation = useInvestorImportDetectConflicts();

  const onDetectConflicts = useEffectEvent((id: string) => {
    if (!hasLoadedConflictsRef.current && !detectConflictsMutation.isPending) {
      hasLoadedConflictsRef.current = true;
      detectConflictsMutation.mutate(id);
    }
  });

  // Load conflict detection results - only once when component mounts
  useEffect(() => {
    if (sessionId) {
      onDetectConflicts(sessionId);
    }
  }, [sessionId]);

  const conflicts = useMemo<ExistingInvestorConflict[]>(() => {
    const data = detectConflictsMutation.data ?? [];
    return data.map((conflict) => ({
      ...conflict,
      should_overwrite:
        overwriteOverrides[conflict.existing_investor_id] ??
        conflict.should_overwrite,
    }));
  }, [detectConflictsMutation.data, overwriteOverrides]);

  const conflictFieldMapping: ConflictFieldMapping = {
    investor_type: {
      label: 'Investor Type',
      getExistingValue: (_conflict) => 'Current database value', // Would need existing data
      getImportValue: (conflict) => conflict.import_investor_data.investor_type,
    },
    is_individual: {
      label: 'Is Individual',
      getExistingValue: (_conflict) => 'Current database value',
      getImportValue: (conflict) =>
        conflict.import_investor_data.is_individual ? 'Yes' : 'No',
    },
    country: {
      label: 'Country',
      getExistingValue: (_conflict) => 'Current database value',
      getImportValue: (conflict) =>
        conflict.import_investor_data.country || 'Not provided',
    },
  };

  const handleOverwriteToggle = useCallback(
    (conflictId: string, shouldOverwrite: boolean) => {
      setOverwriteOverrides((prev) => ({
        ...prev,
        [conflictId]: shouldOverwrite,
      }));
    },
    []
  );

  const handleToggleExpanded = useCallback((conflictId: string) => {
    setExpandedConflicts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(conflictId)) {
        newSet.delete(conflictId);
      } else {
        newSet.add(conflictId);
      }
      return newSet;
    });
  }, []);

  const handleSetAllOverwrite = useCallback(
    (shouldOverwrite: boolean) => {
      const data = detectConflictsMutation.data ?? [];
      setOverwriteOverrides(
        Object.fromEntries(
          data.map((c) => [c.existing_investor_id, shouldOverwrite])
        )
      );
    },
    [detectConflictsMutation.data]
  );

  // Notify parent when conflicts are ready to be resolved
  useEffect(() => {
    if (conflicts.length > 0) {
      onConflictsResolved(conflicts);
    }
  }, [conflicts, onConflictsResolved]);

  const conflictsToOverwrite = conflicts.filter(
    (c) => c.should_overwrite
  ).length;
  const conflictsToSkip = conflicts.filter((c) => !c.should_overwrite).length;

  // Calculate investors that will actually be imported based on mapped funds
  const mappedFundNames = fundMatches
    .filter((fm) => fm.is_matched)
    .map((fm) => fm.import_fund_name);

  const investorsToImport = validationResult.investors_preview.filter(
    (investor) =>
      investor.fund_names.some((fundName) => mappedFundNames.includes(fundName))
  ).length;

  if (conflicts.length === 0 && !detectConflictsMutation.isPending) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              No Conflicts Detected
            </CardTitle>
            <CardDescription>
              Great! No existing investors were found that conflict with the
              import data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">Ready to Import</p>
                  <p>
                    {investorsToImport} investor
                    {investorsToImport !== 1 ? 's' : ''} from mapped funds can
                    be imported without conflicts.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {detectConflictsMutation.error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {detectConflictsMutation.error instanceof Error
              ? detectConflictsMutation.error.message
              : 'Failed to process conflicts'}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {detectConflictsMutation.isPending && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
                <AlertTriangle className="w-6 h-6 text-gray-600 animate-pulse" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Detecting Conflicts...
              </h3>
              <p className="text-sm text-gray-600">
                Checking for existing investors in the database
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conflict Summary */}
      {!detectConflictsMutation.isPending && conflicts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Existing Investor Conflicts ({conflicts.length})
            </CardTitle>
            <CardDescription>
              These investors already exist in the database. Choose whether to
              overwrite existing data or skip them during import.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-red-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-red-900">
                  Conflicts Found
                </div>
                <div className="text-2xl font-bold text-red-900">
                  {conflicts.length}
                </div>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-orange-900">
                  Will Overwrite
                </div>
                <div className="text-2xl font-bold text-orange-900">
                  {conflictsToOverwrite}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-900">
                  Will Skip
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {conflictsToSkip}
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-900">
                Bulk Actions:
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSetAllOverwrite(true)}
                >
                  Overwrite All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSetAllOverwrite(false)}
                >
                  Skip All
                </Button>
              </div>
            </div>

            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <strong>Default Behavior:</strong> By default, no existing data
                will be overwritten. You must explicitly choose which investors
                to update.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Conflict Details */}
      {!detectConflictsMutation.isPending && conflicts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Conflict Details</CardTitle>
            <CardDescription>
              Review each conflict and decide how to handle it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {conflicts.map((conflict) => {
              const isExpanded = expandedConflicts.has(
                conflict.existing_investor_id
              );

              return (
                <div
                  key={conflict.existing_investor_id}
                  className={`border rounded-lg p-4 ${
                    conflict.should_overwrite
                      ? 'border-orange-200 bg-orange-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {conflict.import_investor_data.is_individual ? (
                          <User className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Building2 className="w-4 h-4 text-purple-600" />
                        )}
                        <h4 className="font-medium text-gray-900">
                          {conflict.existing_investor_name}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          {conflict.conflict_fields.length} field
                          {conflict.conflict_fields.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>

                      <div className="text-sm text-gray-600 mb-3">
                        Import data: {conflict.import_investor_data.name}
                        {conflict.import_investor_data.country && (
                          <span>
                            {' '}
                            • {conflict.import_investor_data.country}
                          </span>
                        )}
                      </div>

                      {/* Conflicting Fields */}
                      <div className="mb-3">
                        <div className="text-xs font-medium text-gray-700 mb-1">
                          Conflicting fields:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {conflict.conflict_fields.map((field) => (
                            <Badge
                              key={field}
                              variant="outline"
                              className="text-xs"
                            >
                              {conflictFieldMapping[field]?.label || field}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Overwrite Toggle */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`overwrite-${conflict.existing_investor_id}`}
                            checked={conflict.should_overwrite}
                            onCheckedChange={(checked) =>
                              handleOverwriteToggle(
                                conflict.existing_investor_id,
                                checked
                              )
                            }
                          />
                          <label
                            htmlFor={`overwrite-${conflict.existing_investor_id}`}
                            className={`text-sm font-medium ${
                              conflict.should_overwrite
                                ? 'text-orange-700'
                                : 'text-gray-700'
                            }`}
                          >
                            {conflict.should_overwrite
                              ? 'Overwrite existing data'
                              : 'Skip this investor'}
                          </label>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleToggleExpanded(conflict.existing_investor_id)
                          }
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {isExpanded ? 'Hide' : 'Show'} Details
                        </Button>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="space-y-3">
                            {conflict.conflict_fields.map((field) => {
                              const mapping = conflictFieldMapping[field];
                              if (!mapping) return null;

                              return (
                                <div
                                  key={field}
                                  className="grid grid-cols-2 gap-4 text-sm"
                                >
                                  <div>
                                    <div className="font-medium text-gray-700">
                                      Current ({mapping.label})
                                    </div>
                                    <div className="text-gray-600 bg-gray-100 p-2 rounded">
                                      {mapping.getExistingValue(conflict)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-700">
                                      Import Data ({mapping.label})
                                    </div>
                                    <div className="text-gray-600 bg-blue-50 p-2 rounded">
                                      {mapping.getImportValue(conflict)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Status Summary */}
      {!detectConflictsMutation.isPending && conflicts.length > 0 && (
        <div className="text-sm text-gray-600">
          {conflictsToOverwrite > 0 ? (
            <span className="text-orange-600">
              {conflictsToOverwrite} investor
              {conflictsToOverwrite !== 1 ? 's' : ''} will be updated
            </span>
          ) : (
            <span className="text-gray-600">
              All conflicting investors will be skipped
            </span>
          )}
        </div>
      )}
    </div>
  );
}
